import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { furnitureApi, catalogApi, furnitureTypesApi, movementsApi } from '../services/api';
import { useOfflineSync } from '../hooks/useOffline';
import { usePreferences } from '../contexts/PreferencesContext';
import { useCascadeLocation } from '../hooks/useCascadeLocation';
import { cacheManager } from '../services/cacheManager';
import { ArrowLeft, Save, MapPin, BookOpen, X, WifiOff, CloudOff, Image } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, Package } from 'lucide-react';
import { toast } from 'sonner';
import CatalogSelectionDialog from '../components/furniture/CatalogSelectionDialog';

const STATUS_OPTIONS = [
  { value: 'neuf', label: 'Neuf' },
  { value: 'bon', label: 'Bon' },
  { value: 'usage', label: 'Usagé' },
  { value: 'hors_service', label: 'Hors service' },
  { value: 'en_maintenance', label: 'En maintenance' }
];

const FurnitureFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { isOnline, queueFurnitureCreate } = useOfflineSync();
  const { formatCurrency, getCurrencySymbol } = usePreferences();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [error, setError] = useState('');

  // Track original location to detect changes and create transfer movement
  const [originalLocationId, setOriginalLocationId] = useState(null);
  const [originalLocationData, setOriginalLocationData] = useState({ site_id: null, batiment: '', etage: '', bureau: '' });

  // Location cascade (extracted hook)
  const {
    sites, batiments, etages, bureaux,
    selectedSite, selectedBatiment, selectedEtage, selectedBureau,
    selectedLocationId, locationPath, isOffline: locationOffline,
    setSelectedSite, setSelectedBatiment, setSelectedEtage, setSelectedBureau,
    loadSites, restoreFromLocationId,
  } = useCascadeLocation();

  const [formData, setFormData] = useState({
    code_barres: '',
    type_mobilier: 'bureau',
    marque: '',
    fournisseur: '',
    modele: '',
    reference: '',
    date_acquisition: '',
    cout_achat: '',
    etat: 'neuf',
    service: '',
    collaborateur: '',
    notes: '',
    catalog_item_id: ''
  });

  // Catalog
  const [catalogItems, setCatalogItems] = useState([]);
  const [showCatalogDialog, setShowCatalogDialog] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);

  // Furniture types
  const [furnitureTypes, setFurnitureTypes] = useState([]);

  useEffect(() => {
    loadInitialData();
    if (isEditing) loadFurniture();
  }, [id, isOnline]);

  // Auto-select catalog item when editing and catalog is loaded
  useEffect(() => {
    if (isEditing && formData.catalog_item_id && catalogItems.length > 0 && !selectedCatalogItem) {
      const item = catalogItems.find(c => c.id === formData.catalog_item_id);
      if (item) setSelectedCatalogItem(item);
    }
  }, [isEditing, formData.catalog_item_id, catalogItems, selectedCatalogItem]);

  const loadInitialData = async () => {
    await Promise.all([loadSites(), loadCatalog(), loadFurnitureTypes()]);
  };

  const loadFurnitureTypes = async () => {
    try {
      const response = await furnitureTypesApi.getAll();
      setFurnitureTypes(response.data);
    } catch {
      try {
        const cached = await cacheManager.getCachedData('/api/furniture-types');
        if (cached?.length) setFurnitureTypes(cached);
      } catch (e) {
        console.error('[FurnitureForm] Cache error for types:', e);
      }
    }
  };

  const loadCatalog = async () => {
    try {
      const response = await catalogApi.getAll();
      setCatalogItems(response.data);
    } catch {
      try {
        const cached = await cacheManager.getCachedData('/api/catalog');
        if (cached?.length) setCatalogItems(cached);
      } catch (e) {
        console.error('[FurnitureForm] Cache error for catalog:', e);
      }
    }
  };

  const loadFurniture = async () => {
    try {
      const response = await furnitureApi.getById(id);
      const data = response.data;
      setFormData({
        code_barres: data.code_barres || '',
        type_mobilier: data.type_mobilier || 'bureau',
        marque: data.marque || '',
        fournisseur: data.fournisseur || '',
        modele: data.modele || '',
        reference: data.reference || '',
        date_acquisition: data.date_acquisition || '',
        cout_achat: data.cout_achat ? String(data.cout_achat) : '',
        etat: data.etat || 'neuf',
        service: data.service || '',
        collaborateur: data.collaborateur || '',
        notes: data.notes || '',
        catalog_item_id: data.catalog_item_id || ''
      });
      setOriginalLocationId(data.location_id || null);
      setOriginalLocationData({
        site_id: data.site_id || null,
        batiment: data.batiment || '',
        etage: data.etage || '',
        bureau: data.bureau || '',
      });
      if (data.location_id) await restoreFromLocationId(data.location_id);
    } catch (err) {
      console.error('Erreur chargement mobilier:', err);
      setError('Impossible de charger les données du mobilier');
    } finally {
      setLoadingData(false);
    }
  };

  const selectFromCatalog = (item) => {
    setFormData(prev => ({
      ...prev,
      type_mobilier: item.type_mobilier,
      marque: item.marque || '',
      modele: item.modele || '',
      fournisseur: item.fournisseur || '',
      cout_achat: item.cout_unitaire ? String(item.cout_unitaire) : '',
      catalog_item_id: item.id
    }));
    setSelectedCatalogItem(item);
    setShowCatalogDialog(false);
  };

  const clearCatalogSelection = () => {
    setSelectedCatalogItem(null);
    setFormData(prev => ({ ...prev, catalog_item_id: '' }));
  };

  const filteredCatalogItems = catalogItems.filter(item => {
    if (!catalogSearch) return true;
    const search = catalogSearch.toLowerCase();
    return (
      item.marque?.toLowerCase().includes(search) ||
      item.modele?.toLowerCase().includes(search) ||
      item.fournisseur?.toLowerCase().includes(search)
    );
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = {
        ...formData,
        cout_achat: formData.cout_achat ? parseFloat(formData.cout_achat) : null,
        location_id: selectedLocationId
      };
      if (isEditing) {
        await furnitureApi.update(id, payload);
        if (selectedLocationId !== originalLocationId) {
          const batimentNom = batiments.find(b => b.id === selectedBatiment)?.nom || '';
          const etageNom = etages.find(e => e.id === selectedEtage)?.nom || '';
          const bureauNom = bureaux.find(b => b.id === selectedBureau)?.nom || '';
          try {
            await movementsApi.create({
              furniture_id: id,
              type_mouvement: 'transfert',
              site_origine_id: originalLocationData.site_id,
              batiment_origine: originalLocationData.batiment,
              etage_origine: originalLocationData.etage,
              bureau_origine: originalLocationData.bureau,
              site_destination_id: selectedSite || null,
              batiment_destination: batimentNom,
              etage_destination: etageNom,
              bureau_destination: bureauNom,
            });
          } catch (mvtErr) {
            console.error('Erreur création mouvement de transfert:', mvtErr);
          }
        }
      } else {
        await furnitureApi.create(payload);
      }
      navigate('/furniture');
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      if (!isEditing) {
        try {
          const payload = {
            ...formData,
            cout_achat: formData.cout_achat ? parseFloat(formData.cout_achat) : null,
            location_id: selectedLocationId
          };
          await queueFurnitureCreate(payload);
          toast.success('Mobilier enregistré localement', {
            description: 'Sera synchronisé automatiquement quand la connexion sera rétablie'
          });
          navigate('/furniture');
          return;
        } catch (queueError) {
          console.error('Erreur mise en file:', queueError);
          setError('Impossible de sauvegarder le mobilier. Vérifiez votre connexion.');
        }
      } else {
        setError('La modification de mobilier nécessite une connexion Internet');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const offlineMode = locationOffline && !isOnline;

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="furniture-form-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/furniture')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">
            {isEditing ? 'Modifier le mobilier' : 'Nouveau mobilier'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isEditing ? 'Modifiez les informations du mobilier' : "Ajoutez un nouveau mobilier à l'inventaire"}
          </p>
        </div>
        {offlineMode && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full">
            <WifiOff className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Mode hors-ligne</span>
          </div>
        )}
      </div>

      {offlineMode && !isEditing && (
        <Alert className="bg-blue-50 border-blue-200">
          <CloudOff className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>Mode hors-ligne :</strong> Le mobilier sera enregistré localement et synchronisé automatiquement quand la connexion sera rétablie.
          </AlertDescription>
        </Alert>
      )}

      {offlineMode && isEditing && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            La modification de mobilier n'est pas disponible en mode hors-ligne. Veuillez vous reconnecter à Internet.
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          {/* Catalog image preview */}
          {selectedCatalogItem?.image_url && (
            <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-white">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4 items-center">
                  <div className="relative w-full md:w-48 h-48 rounded-lg overflow-hidden bg-white shadow-sm border">
                    <img
                      src={selectedCatalogItem.image_url}
                      alt={`${selectedCatalogItem.marque} ${selectedCatalogItem.modele}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                      <Image className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-medium text-blue-600">Photo du catalogue</span>
                    </div>
                    <h3 className="text-xl font-semibold text-slate-800">{selectedCatalogItem.marque}</h3>
                    <p className="text-slate-600">{selectedCatalogItem.modele}</p>
                    {selectedCatalogItem.description && (
                      <p className="text-sm text-slate-500 mt-2 line-clamp-2">{selectedCatalogItem.description}</p>
                    )}
                    {selectedCatalogItem.cout_unitaire && (
                      <p className="text-sm font-medium text-emerald-600 mt-2">
                        Valeur catalogue : {formatCurrency(selectedCatalogItem.cout_unitaire)}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Identification */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Identification</CardTitle>
                <Button
                  type="button" variant="outline" size="sm"
                  onClick={() => setShowCatalogDialog(true)}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  data-testid="select-from-catalog-btn"
                >
                  <BookOpen className="w-4 h-4 mr-2" />Choisir du catalogue
                </Button>
              </div>
              {selectedCatalogItem && !selectedCatalogItem.image_url && (
                <div className="mt-2 flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded bg-white flex items-center justify-center">
                    <Package className="w-5 h-5 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-900 truncate">{selectedCatalogItem.marque} - {selectedCatalogItem.modele}</p>
                    <p className="text-xs text-blue-600">Depuis le catalogue</p>
                  </div>
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={clearCatalogSelection}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              {selectedCatalogItem?.image_url && (
                <div className="mt-2 flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <span className="font-medium">{selectedCatalogItem.marque} - {selectedCatalogItem.modele}</span>
                    <span className="text-blue-500 ml-2">• Depuis le catalogue</span>
                  </p>
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={clearCatalogSelection}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code_barres">Code-barres *</Label>
                  <Input id="code_barres" name="code_barres" value={formData.code_barres} onChange={handleChange} placeholder="Ex: MOB-2024-001" required disabled={isEditing} className="font-mono" data-testid="input-code-barres" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type_mobilier">Type de mobilier *</Label>
                  <Select value={formData.type_mobilier} onValueChange={(v) => handleSelectChange('type_mobilier', v)}>
                    <SelectTrigger data-testid="select-type"><SelectValue placeholder="Sélectionner un type" /></SelectTrigger>
                    <SelectContent>
                      {furnitureTypes.map(ft => <SelectItem key={ft.code} value={ft.code}>{ft.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="marque">Marque</Label>
                  <Input id="marque" name="marque" value={formData.marque} onChange={handleChange} placeholder="Ex: Herman Miller" data-testid="input-marque" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modele">Modèle</Label>
                  <Input id="modele" name="modele" value={formData.modele} onChange={handleChange} placeholder="Ex: Aeron" data-testid="input-modele" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fournisseur">Fournisseur</Label>
                  <Input id="fournisseur" name="fournisseur" value={formData.fournisseur} onChange={handleChange} placeholder="Ex: Office Depot" data-testid="input-fournisseur" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reference">Référence</Label>
                  <Input id="reference" name="reference" value={formData.reference} onChange={handleChange} placeholder="Ex: AER-BLK-001" className="font-mono" data-testid="input-reference" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Acquisition */}
          <Card>
            <CardHeader><CardTitle className="font-heading text-lg">Acquisition</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_acquisition">Date d&apos;acquisition</Label>
                  <Input id="date_acquisition" name="date_acquisition" type="date" value={formData.date_acquisition} onChange={handleChange} data-testid="input-date" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cout_achat">Coût d&apos;achat ({getCurrencySymbol()})</Label>
                  <Input id="cout_achat" name="cout_achat" type="number" step="0.01" value={formData.cout_achat} onChange={handleChange} placeholder="0.00" data-testid="input-cout" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="etat">État *</Label>
                  <Select value={formData.etat} onValueChange={(v) => handleSelectChange('etat', v)}>
                    <SelectTrigger data-testid="select-etat"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Localisation */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-slate-400" />Localisation
              </CardTitle>
              {locationPath && <p className="text-sm text-blue-600 font-medium">{locationPath}</p>}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Site', items: sites, value: selectedSite, setter: setSelectedSite, testId: 'select-site', placeholder: 'Sélectionner un site', disabled: false },
                  { label: 'Bâtiment', items: batiments, value: selectedBatiment, setter: setSelectedBatiment, testId: 'select-batiment', placeholder: batiments.length === 0 ? 'Aucun bâtiment' : 'Sélectionner', disabled: !selectedSite || batiments.length === 0 },
                  { label: 'Étage', items: etages, value: selectedEtage, setter: setSelectedEtage, testId: 'select-etage', placeholder: etages.length === 0 ? 'Aucun étage' : 'Sélectionner', disabled: !selectedBatiment || etages.length === 0 },
                  { label: 'Bureau', items: bureaux, value: selectedBureau, setter: setSelectedBureau, testId: 'select-bureau', placeholder: bureaux.length === 0 ? 'Aucun bureau' : 'Sélectionner', disabled: !selectedEtage || bureaux.length === 0 },
                ].map(({ label, items, value, setter, testId, placeholder, disabled }) => (
                  <div key={label} className="space-y-2">
                    <Label>{label}</Label>
                    <Select value={value} onValueChange={setter} disabled={disabled}>
                      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
                      <SelectContent>
                        {items.map(item => <SelectItem key={item.id} value={item.id}>{item.nom}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <Label htmlFor="service">Service</Label>
                  <Input id="service" name="service" value={formData.service} onChange={handleChange} placeholder="Ex: Comptabilité" data-testid="input-service" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="collaborateur">Collaborateur affecté</Label>
                  <Input id="collaborateur" name="collaborateur" value={formData.collaborateur} onChange={handleChange} placeholder="Ex: Jean Dupont" data-testid="input-collaborateur" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader><CardTitle className="font-heading text-lg">Notes</CardTitle></CardHeader>
            <CardContent>
              <Textarea name="notes" value={formData.notes} onChange={handleChange} placeholder="Informations complémentaires..." rows={4} data-testid="input-notes" />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/furniture')}>Annuler</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 btn-scale" disabled={loading} data-testid="submit-btn">
              {loading ? 'Enregistrement...' : (
                <><Save className="w-4 h-4 mr-2" />{isEditing ? 'Mettre à jour' : 'Créer le mobilier'}</>
              )}
            </Button>
          </div>
        </div>
      </form>

      <CatalogSelectionDialog
        open={showCatalogDialog}
        onOpenChange={setShowCatalogDialog}
        items={filteredCatalogItems}
        search={catalogSearch}
        onSearchChange={setCatalogSearch}
        onSelect={selectFromCatalog}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default FurnitureFormPage;
